import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus } from 'lucide-react';
import { describeDaysLeft } from '@bits/domain';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  RenewalCard,
  RenewalDialog,
  useRenewalQuickActions,
  type RenewalDialogState,
} from '@/components/renewals';

export function ProjectRenewalsTab({ projectId }: { projectId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = React.useState<RenewalDialogState>(null);
  const [showArchived, setShowArchived] = React.useState(false);

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canEdit = (meQuery.data?.person.roles ?? []).some((r) => r === 'Admin' || r === 'PM');

  const invalidate = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['projects'] });
    await queryClient.invalidateQueries({ queryKey: ['renewals'] });
    await queryClient.invalidateQueries({ queryKey: ['renewals-due'] });
  }, [queryClient, projectId]);

  const quick = useRenewalQuickActions(invalidate);

  const renewals = projectQuery.data?.renewals ?? [];
  const active = renewals.filter((r) => r.archivedAt === null);
  const archived = renewals.filter((r) => r.archivedAt !== null);
  const attention = active.filter((r) => r.daysLeft !== null && r.daysLeft <= 30);

  if (projectQuery.isPending) {
    return <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-xl font-semibold">Hosting, domain & AMC</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Renewals linked to this project. Every renewal across the company is on the{' '}
            <Link to="/renewals" className="font-medium text-[hsl(var(--brand))] hover:underline">
              Renewals page
            </Link>
            , where it can also be unlinked or moved to another project.
          </p>
        </div>
        {canEdit ? (
          <Button size="sm" onClick={() => setDialog({ mode: 'add', projectId })}>
            <Plus />
            Add renewal
          </Button>
        ) : null}
      </div>

      {quick.errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{quick.errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      {attention.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>
            {attention.length === 1
              ? '1 renewal needs attention'
              : `${attention.length} renewals need attention`}
          </AlertTitle>
          <AlertDescription>
            {attention.map((r) => `${r.label} ${describeDaysLeft(r.daysLeft ?? 0)}`).join(' · ')}
          </AlertDescription>
        </Alert>
      ) : null}

      {active.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
              <CalendarClock className="size-5" aria-hidden />
            </span>
            <div className="flex flex-col gap-1">
              <p className="font-medium">No hosting, domain or AMC details yet</p>
              <p className="max-w-sm text-sm text-[hsl(var(--muted-foreground))]">
                Record the domain, hosting plan, SSL and AMC with their expiry dates to get
                reminders before they lapse.
              </p>
            </div>
            {canEdit ? (
              <Button size="sm" onClick={() => setDialog({ mode: 'add', projectId })}>
                <Plus />
                Add renewal
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {active.map((r) => (
            <li key={r.id}>
              <RenewalCard
                renewal={r}
                canEdit={canEdit}
                busy={quick.busy}
                onEdit={() => setDialog({ mode: 'edit', renewal: r })}
                onRenewed={() => quick.renewed(r)}
                onArchive={() => quick.archive(r)}
              />
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="self-start text-sm text-[hsl(var(--muted-foreground))] underline-offset-4 hover:underline"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide' : 'Show'} {archived.length} archived
          </button>
          {showArchived ? (
            <ul className="grid gap-3 md:grid-cols-2">
              {archived.map((r) => (
                <li key={r.id}>
                  <RenewalCard
                    renewal={r}
                    canEdit={canEdit}
                    busy={quick.busy}
                    onEdit={() => setDialog({ mode: 'edit', renewal: r })}
                    onRestore={() => quick.restore(r)}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <RenewalDialog
        state={dialog}
        lockProject
        onClose={() => setDialog(null)}
        onSaved={async () => {
          setDialog(null);
          quick.clearError();
          await invalidate();
        }}
      />
    </div>
  );
}
